/**
 * iter87.199.43 · TOTP 2FA enrollment / status / disable / regenerate card.
 *
 * Mounted from:
 *   - /admin  → components/site-admin/AccountTab.jsx
 *   - /platform → pages/PlatformConsole.jsx (inline AccountTab)
 *
 * The backend derives the real role from the session cookie, so this
 * component doesn't need to know whether it's rendering for a Site
 * Admin or a Platform Admin — the four states (loading, disabled,
 * enrolling, enabled) drive the entire flow.
 *
 * Design contract: `memory/COMMONS_ADMIN_TOTP_2FA_DESIGN.md` §4.1.
 *
 * Backup codes are shown ONCE at enrollment. Regenerating replaces them
 * (old codes invalidated). Disable requires a fresh 6-digit code.
 */
import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ShieldCheck, ShieldAlert, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const _pluralize = (n, one, many) => (n === 1 ? one : many);

function BackupCodesPanel({ codes, onAcknowledged }) {
    const [copied, setCopied] = useState(false);
    const [acked, setAcked] = useState(false);

    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(codes.join("\n"));
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Backup codes copied — paste into your password manager");
        } catch {
            toast.error("Clipboard access blocked — copy them manually");
        }
    };

    return (
        <div className="border border-volt/40 bg-black/40 p-3 space-y-3" data-testid="totp-backup-codes-panel">
            <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-volt shrink-0 mt-0.5" />
                <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-volt">
                        Save these {codes.length} backup codes now — shown once
                    </p>
                    <p className="text-[11px] text-zinc-300 mt-1 leading-relaxed">
                        Store them in your password manager. Each code works once if you lose your authenticator.
                        If both codes AND authenticator are lost, only Emergent Support can reset your access.
                    </p>
                </div>
            </div>
            <ol className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-white" data-testid="totp-backup-codes-list">
                {codes.map((c) => (
                    <li key={c} className="tabular-nums">{c}</li>
                ))}
            </ol>
            <div className="flex flex-col sm:flex-row gap-2">
                <button
                    type="button"
                    onClick={doCopy}
                    className="brutal-btn-secondary text-xs px-3 py-2 flex items-center gap-1.5 justify-center flex-1"
                    data-testid="totp-backup-copy-btn"
                >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy all"}
                </button>
                <label className="flex items-center gap-2 text-[11px] text-zinc-300 flex-1">
                    <input
                        type="checkbox"
                        checked={acked}
                        onChange={(e) => {
                            setAcked(e.target.checked);
                            if (e.target.checked) onAcknowledged();
                        }}
                        data-testid="totp-backup-ack-checkbox"
                    />
                    I've saved these codes somewhere safe
                </label>
            </div>
        </div>
    );
}

function EnrollingPanel({ otpauthUri, backupCodes, onVerified, onCancel }) {
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [ackDone, setAckDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (busy || code.length !== 6) return;
        setBusy(true);
        try {
            await api.authTotpEnrollVerify(code);
            toast.success("Two-factor authentication is enabled");
            onVerified();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Invalid code";
            toast.error(detail);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <p className="text-xs text-zinc-300 leading-relaxed mb-3">
                    Open your authenticator app (Apple Passwords, Google Authenticator, 1Password, Authy, Bitwarden),
                    tap the "+" icon, and scan this QR code. You'll see a new 6-digit code refresh every 30 seconds.
                </p>
                <div className="bg-white p-4 inline-block border-2 border-black" data-testid="totp-qr">
                    <QRCodeSVG value={otpauthUri} size={192} />
                </div>
            </div>
            <details className="text-xs text-zinc-400">
                <summary className="cursor-pointer hover:text-zinc-300">Can't scan? Copy the setup link instead</summary>
                <pre className="text-[10px] text-zinc-300 bg-black/40 border border-white/10 p-2 mt-2 overflow-x-auto" data-testid="totp-otpauth-uri">
                    {otpauthUri}
                </pre>
            </details>
            <BackupCodesPanel codes={backupCodes} onAcknowledged={() => setAckDone(true)} />
            <form onSubmit={submit} className="space-y-3">
                <div>
                    <label className="text-[11px] font-black uppercase tracking-widest text-cyber block mb-1">
                        Enter the 6-digit code from your authenticator
                    </label>
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        pattern="\d{6}"
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="brutal-input w-40 font-mono tracking-widest text-lg"
                        data-testid="totp-verify-input"
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        type="submit"
                        disabled={busy || code.length !== 6 || !ackDone}
                        className="brutal-btn-primary text-xs px-3 py-2 flex-1"
                        data-testid="totp-verify-btn"
                    >
                        {busy ? "Verifying…" : "Verify & enable"}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="brutal-btn-secondary text-xs px-3 py-2"
                        data-testid="totp-enroll-cancel-btn"
                    >
                        Cancel
                    </button>
                </div>
                {!ackDone && (
                    <p className="text-[10px] text-zinc-500">
                        Tick the "saved" box above to enable the button.
                    </p>
                )}
            </form>
        </div>
    );
}

function DisableConfirmPanel({ onConfirmed, onCancel }) {
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (busy || code.length !== 6) return;
        setBusy(true);
        try {
            await api.authTotpDisable(code);
            toast.success("Two-factor authentication disabled");
            onConfirmed();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid code");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="border border-red-500/40 bg-red-500/5 p-3 space-y-3" data-testid="totp-disable-panel">
            <p className="text-[11px] font-black uppercase tracking-widest text-red-400">
                Confirm disable
            </p>
            <p className="text-xs text-zinc-300 leading-relaxed">
                Enter a fresh 6-digit code from your authenticator to turn off 2FA on this account.
                Your backup codes will be invalidated.
            </p>
            <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="\d{6}"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="brutal-input w-40 font-mono tracking-widest"
                data-testid="totp-disable-input"
            />
            <div className="flex gap-2">
                <button
                    type="submit"
                    disabled={busy || code.length !== 6}
                    className="brutal-btn-primary text-xs px-3 py-2 flex-1"
                    data-testid="totp-disable-confirm-btn"
                >
                    {busy ? "Disabling…" : "Disable authenticator"}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="brutal-btn-secondary text-xs px-3 py-2"
                    data-testid="totp-disable-cancel-btn"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

function RegenerateConfirmPanel({ onRegenerated, onCancel }) {
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [newCodes, setNewCodes] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (busy || code.length !== 6) return;
        setBusy(true);
        try {
            const r = await api.authTotpRegenerateBackupCodes(code);
            setNewCodes(r.backup_codes);
            toast.success("New backup codes generated — old ones invalidated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid code");
        } finally {
            setBusy(false);
        }
    };

    if (newCodes) {
        return (
            <div className="space-y-3" data-testid="totp-regen-result-panel">
                <BackupCodesPanel codes={newCodes} onAcknowledged={() => { }} />
                <button
                    type="button"
                    onClick={onRegenerated}
                    className="brutal-btn-primary text-xs px-3 py-2 w-full"
                    data-testid="totp-regen-done-btn"
                >
                    Done — I've saved the new codes
                </button>
            </div>
        );
    }
    return (
        <form onSubmit={submit} className="border border-volt/40 bg-black/40 p-3 space-y-3" data-testid="totp-regen-panel">
            <p className="text-[11px] font-black uppercase tracking-widest text-volt">
                Regenerate backup codes
            </p>
            <p className="text-xs text-zinc-300 leading-relaxed">
                Enter a fresh 6-digit code from your authenticator. The 10 new codes will replace all existing ones — any
                unused code you have saved will stop working.
            </p>
            <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="\d{6}"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="brutal-input w-40 font-mono tracking-widest"
                data-testid="totp-regen-input"
            />
            <div className="flex gap-2">
                <button
                    type="submit"
                    disabled={busy || code.length !== 6}
                    className="brutal-btn-primary text-xs px-3 py-2 flex-1"
                    data-testid="totp-regen-confirm-btn"
                >
                    {busy ? "Regenerating…" : "Generate new codes"}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="brutal-btn-secondary text-xs px-3 py-2"
                    data-testid="totp-regen-cancel-btn"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

export default function TotpCard() {
    const [status, setStatus] = useState(null);  // { enabled, backup_codes_remaining, ... } | null
    const [enrollment, setEnrollment] = useState(null);  // { otpauth_uri, backup_codes } | null
    const [confirmingDisable, setConfirmingDisable] = useState(false);
    const [confirmingRegen, setConfirmingRegen] = useState(false);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const r = await api.authTotpStatus();
            setStatus(r);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load 2FA status");
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const startEnroll = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const r = await api.authTotpEnroll();
            setEnrollment(r);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start enrollment");
        } finally {
            setBusy(false);
        }
    };

    const onVerified = async () => {
        setEnrollment(null);
        await refresh();
    };

    const onDisabled = async () => {
        setConfirmingDisable(false);
        await refresh();
    };

    const onRegenerated = async () => {
        setConfirmingRegen(false);
        await refresh();
    };

    if (status === null) {
        return (
            <div className="border-t border-white/10 pt-4 mt-4" data-testid="totp-card-loading">
                <p className="text-xs text-zinc-500">Loading 2FA status…</p>
            </div>
        );
    }

    return (
        <div className="border-t border-white/10 pt-4 mt-4 space-y-3" data-testid="totp-card">
            <div className="flex items-start gap-2">
                {status.enabled ? (
                    <ShieldCheck className="w-4 h-4 text-cyber shrink-0 mt-0.5" data-testid="totp-status-enabled-icon" />
                ) : (
                    <ShieldAlert className="w-4 h-4 text-volt shrink-0 mt-0.5" data-testid="totp-status-disabled-icon" />
                )}
                <div className="flex-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-cyber">
                        Two-factor authentication (TOTP)
                    </p>
                    {status.enabled ? (
                        <p className="text-xs text-zinc-300 mt-1" data-testid="totp-status-line">
                            Enabled — a 6-digit code is required at sign-in.{" "}
                            <span
                                className={status.backup_codes_remaining <= 2 ? "text-red-400 font-black" : "text-zinc-400"}
                                data-testid="totp-status-remaining"
                            >
                                {status.backup_codes_remaining} backup {_pluralize(status.backup_codes_remaining, "code", "codes")} remaining.
                            </span>
                        </p>
                    ) : (
                        <p className="text-xs text-zinc-300 mt-1" data-testid="totp-status-line">
                            Not enabled. Turn on to require an authenticator code at every sign-in.
                        </p>
                    )}
                </div>
            </div>

            {enrollment && (
                <EnrollingPanel
                    otpauthUri={enrollment.otpauth_uri}
                    backupCodes={enrollment.backup_codes}
                    onVerified={onVerified}
                    onCancel={() => setEnrollment(null)}
                />
            )}

            {!enrollment && !status.enabled && (
                <button
                    type="button"
                    onClick={startEnroll}
                    disabled={busy}
                    className="brutal-btn-primary text-xs px-3 py-2"
                    data-testid="totp-enable-btn"
                >
                    {busy ? "Preparing…" : "Enable authenticator"}
                </button>
            )}

            {!enrollment && status.enabled && !confirmingDisable && !confirmingRegen && (
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setConfirmingRegen(true)}
                        className="brutal-btn-secondary text-xs px-3 py-2 flex items-center gap-1.5"
                        data-testid="totp-regen-btn"
                    >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Regenerate backup codes
                    </button>
                    <button
                        type="button"
                        onClick={() => setConfirmingDisable(true)}
                        className="brutal-btn-secondary text-xs px-3 py-2 border-red-500/60 text-red-400 hover:text-red-300"
                        data-testid="totp-disable-btn"
                    >
                        Disable authenticator
                    </button>
                </div>
            )}

            {confirmingDisable && (
                <DisableConfirmPanel
                    onConfirmed={onDisabled}
                    onCancel={() => setConfirmingDisable(false)}
                />
            )}

            {confirmingRegen && (
                <RegenerateConfirmPanel
                    onRegenerated={onRegenerated}
                    onCancel={() => setConfirmingRegen(false)}
                />
            )}
        </div>
    );
}
